import { Injectable, inject } from "@angular/core";
import { LoggerService } from "./logger.service";
import { CloudProvider } from "../models/vcloud-api";
import { Csp, DateGranularity, DetailLevel, JobRole, Page, Section } from "../models/vcloudsmart";
import { FormattedValue } from "../models/value-formatting";
import { UserPermissions } from "../models/tenancies";
import {
  awsDefinitions,
  azureDefinitions,
  drccDefinitions,
  gcpDefinitions,
  markdownConverter,
  ociDefinitions,
} from "../constant/definitions";
import { environment } from "src/environments/environment";
import { Definition } from "../models/definitions";
import { CSP } from "../models/csp-api";
import { FeatureTogglesService } from "./feature-toggles.service";
import { MAX_API_RESPONSE_LIMIT, ROWS_PER_PAGE } from "../constant/vcloud-api";
import Swal from "sweetalert2";
import { CustomReport, CustomSection, CustomVCSReport, ReportShare } from "../models/report-sharing";
import { IconPack } from "../models/icons";
import { forkJoin, Observable, of } from "rxjs";
import { map } from "rxjs/operators";
import { AuthorisationService } from "./authorisation.service";
import { PcsApiService } from "./pcs-api.service";
import { FormattingService } from "./formatting.service";
import { DEFAULT_ICON_PACK } from "../constant/icons";
import { QueryDomain } from "../models/llm";
import { transformLegacyPageName, transformLegacySectionName } from "../constant/looker-studio";
import { isDisabledDay } from "ngx-bootstrap/chronos";

@Injectable({
  providedIn: "root",
})
export class LookerStudioService {
  private log = inject(LoggerService);
  formatting = inject(FormattingService);
  private featureToggles = inject(FeatureTogglesService);
  private pcsApi = inject(PcsApiService);
  private authorisation = inject(AuthorisationService);

  private definitionsIndexed: { [csp: string]: { [term: string]: Definition } } = {
    aws: Object.fromEntries(awsDefinitions.map((d) => [d.term, d])),
    azure: Object.fromEntries(azureDefinitions.map((d) => [d.term, d])),
    gcp: Object.fromEntries(gcpDefinitions.map((d) => [d.term, d])),
    oci: Object.fromEntries(ociDefinitions.map((d) => [d.term, d])),
    drcc: Object.fromEntries(drccDefinitions.map((d) => [d.term, d])),
  };

  private markdownCache: { [csp: string]: { [term: string]: string } } = {};

  public getPreferredGranularity(): DateGranularity {
    try {
      let v: string | undefined | null = localStorage.getItem("dateGranularities");
      if (!v) {
        v = "{}";
      }
      let vals: { [url: string]: DateGranularity } = JSON.parse(v);
      if (!vals) {
        vals = {};
      }
      const prefix = (location?.pathname || "")
        .split("/")
        .filter((v) => v !== "/" && v !== "vcloudsmart" && v !== "v2")
        .slice(1, 4)
        .filter(Boolean)
        .join("/");
      return vals[prefix] || "MONTH";
    } catch (e) {
      this.log.error(e);
      localStorage.removeItem("dateGranularities");
      return "MONTH";
    }
  }

  public setPreferredGranularity(g: DateGranularity) {
    try {
      let v: string | undefined | null = localStorage.getItem("dateGranularities");
      if (!v) {
        v = "{}";
      }
      let vals: { [url: string]: DateGranularity } = JSON.parse(v);
      if (!vals) {
        vals = {};
      }
      const prefix = (location?.pathname || "")
        .split("/")
        .filter((v) => v !== "/" && v !== "vcloudsmart" && v !== "v2")
        .slice(1, 4)
        .filter(Boolean)
        .join("/");
      vals[prefix] = g;
      localStorage.setItem("dateGranularities", JSON.stringify(vals));
    } catch (e) {
      this.log.error(e);
      localStorage.removeItem("dateGranularities");
      return "MONTH";
    }
  }

  public getCustomReports(): Observable<CustomReport[]> {
    return this.pcsApi.get<{ Resources: CustomReport[] }>("tenancies-v1/share-codes").pipe(
      map((r) => {
        return (r?.Resources ?? []).filter((r: CustomReport) => {
          const t: string = (r as ReportShare)?.Target || "";
          return !t.includes("/recently-visited/");
        });
      }),
      map((reports: CustomReport[]) => {
        const deduplicatedReports: CustomReport[] = [];
        for (const r of reports) {
          if (
            !deduplicatedReports.some(({ DisplayName, Filters, ...rest }) => {
              return (
                (rest as ReportShare).Target === (r as ReportShare)?.Target &&
                Filters === r?.Filters &&
                DisplayName === r?.DisplayName
              );
            })
          ) {
            deduplicatedReports.push(r);
          }
        }
        return deduplicatedReports;
      }),
    );
  }

  public getApiLimitAndPagesToLoad(
    frontEndTablePageSize: number,
    pageNumber: number,
    lastLoadedPage: number,
    backendTableRowsPerFrontEndTableRow: number = 1,
  ): { frontEndTablePagesToLoad: number; backEndTableRowsToLoad: number } | undefined {
    const frontEndTablePagesToLoad: number = Math.floor(
      pageNumber - lastLoadedPage + ROWS_PER_PAGE / frontEndTablePageSize / backendTableRowsPerFrontEndTableRow,
    );

    const backEndTableRowsToLoad: number = Math.floor(
      frontEndTablePageSize * frontEndTablePagesToLoad * backendTableRowsPerFrontEndTableRow,
    );

    if (backEndTableRowsToLoad > MAX_API_RESPONSE_LIMIT) {
      Swal.fire({
        title: "Too Many Records",
        icon: "warning",
        text: `Your query would fetch too many (${backEndTableRowsToLoad}) rows.`,
        confirmButtonColor: "#E60000",
        allowOutsideClick: false,
        showCancelButton: false,
        showCloseButton: false,
        showConfirmButton: true,
        confirmButtonText: "OK",
      });
      return undefined;
    } else {
      return { backEndTableRowsToLoad, frontEndTablePagesToLoad };
    }
  }

  // this was added as a part of a task to resolve circular dependency between services
  // I have split the looker studio service into 2 additional services: CurrencyService & FormattingService
  // leaving here for now, cleanup required to reroute everything to the new place
  public prettifyAttribute(attribute: string): string {
    return this.formatting.prettifyAttribute(attribute);
  }

  // this was added as a part of a task to resolve circular dependency between services
  // I have split the looker studio service into 2 additional services: CurrencyService & FormattingService
  // leaving here for now, cleanup required to reroute everything to the new place
  public formatNumber(value: number | null | undefined, decimalPlaces = 2): FormattedValue {
    return this.formatting.formatNumber(value, decimalPlaces);
  }

  // this was added as a part of a task to resolve circular dependency between services
  // I have split the looker studio service into 2 additional services: CurrencyService & FormattingService
  // leaving here for now, cleanup required to reroute everything to the new place
  public formatBytes(bytes: number | null | undefined, decimals = 2): FormattedValue {
    return this.formatting.formatBytes(bytes, decimals);
  }
  public dateFormatter(date: string): Date {
    const year = Number(date.substring(0, 4));
    const month = Number(date.substring(4, 6));
    return new Date(year, month - 1);
  }
  public getDefinition(csp: CSP, term: string): Definition {
    const d = (this.definitionsIndexed[csp] || {})[term];
    if (d) {
      const maybeCached = (this.markdownCache[csp] || {})[term];
      if (maybeCached) {
        return { ...d, definition: maybeCached };
      } else {
        if (!(csp in this.markdownCache)) {
          this.markdownCache[csp] = {};
        }
        const markdown = markdownConverter.makeMarkdown(d.definition);
        this.markdownCache[csp][term] = markdown;
        return { ...d, definition: markdown };
      }
    }
  }

  private getVCSConfigV2(currentTenant: string | undefined, user: UserPermissions, vcsConfig?: Array<Csp>): Array<Csp> {
    return (vcsConfig ?? this.getVCSConfigV2Helper(currentTenant, user)).map((csp) => {
      return {
        ...csp,
        sections: csp.sections.map((section) => {
          return {
            ...section,
            title: transformLegacySectionName(section.title),
            pages: section.pages.map((page) => {
              return {
                ...page,
                title: transformLegacySectionName(page.title),
              };
            }),
          };
        }),
      };
    });
  }

  private baseCsps(currentTenant: string, userInfo: UserPermissions): Csp[] {
    return [
      {
        id: "aws",
        title: "Amazon Web Services",
        icon: "/assets/icons-home/aws.svg",
        termsAndDefinitions: awsDefinitions,
        sections: [
          {
            title: "Cost & Billing",
            description: "Reports related to cost & billing of resources, services, accounts etc. over time.",
            pages: [
              {
                title: "Overview",
                isMigrated: true,
                iconName: "calendar",
                iconPack: "source-mid-render-light-icons",
                description: "Summary of costs over time, top cost contributors & cost grouped by service.",
                detailLevel: "LOW",
                jobRoles: ["Finance", "Engineering", "Leadership", "FinOps"],
                target:
                  "https://lookerstudio.google.com/embed/reporting/fa865367-b6f0-48b2-8009-d8064ed075c4/page/CCynB",
                styles: {
                  minHeight: "350vh",
                },
              },
              {
                title: "Charge Analysis (SAMS)",
                isMigrated: true,
                iconName: "pie-chart",
                isHidden: currentTenant !== "vodafone",
                iconPack: "source-mid-render-light-icons",
                detailLevel: "HIGH",
                jobRoles: ["Finance", "Leadership"],
                description:
                  "The report focuses on PCS & VPC charges added on top of the AWS Actual Cost. Use this report to understand internal charges.",
                isDisabled: currentTenant !== "vodafone",
                target:
                  "https://lookerstudio.google.com/embed/reporting/fa865367-b6f0-48b2-8009-d8064ed075c4/page/p_snh8j0lwvc",
                styles: {
                  minHeight: "270vh",
                },
              },
              {
                title: "Charge Analysis (SAMS) TEST",
                isMigrated: true,
                iconName: "pie-chart",
                isHidden:
                  currentTenant !== "vodafone" || !this.featureToggles.isFeatureEnabled(userInfo, "aws-vams-test"),
                iconPack: "source-mid-render-light-icons",
                detailLevel: "HIGH",
                jobRoles: ["Finance", "Leadership"],
                description:
                  "The report focuses on PCS & VPC charges added on top of the AWS Actual Cost. Use this report to understand internal charges.",
                isDisabled:
                  currentTenant !== "vodafone" || !this.featureToggles.isFeatureEnabled(userInfo, "aws-vams-test"),
                target:
                  "https://lookerstudio.google.com/embed/reporting/fa865367-b6f0-48b2-8009-d8064ed075c4/page/p_snh8j0lwvc",
                styles: {
                  minHeight: "270vh",
                },
              },
              {
                title: "Cost Analysis",
                isMigrated: true,
                detailLevel: "HIGH",
                jobRoles: ["Leadership", "Engineering", "Finance", "FinOps"],
                description: "Analysis of cost by various attributes such as: Account, Environment, Organisation etc.",
                iconName: "chart-bar",
                iconPack: "source-mid-render-light-icons",
                target:
                  "https://lookerstudio.google.com/embed/reporting/fa865367-b6f0-48b2-8009-d8064ed075c4/page/p_l4s4ohmvuc",
                styles: {
                  minHeight: "350vh",
                },
              },
              {
                title: "Data Transfer Analysis",
                isMigrated: true,
                detailLevel: "HIGH",
                jobRoles: ["Engineering", "FinOps", "Operations"],
                description: "Analysis of data transfer (ingress/egress) costs",
                iconName: "network-tower",
                iconPack: "source-mid-render-light-icons",
              },
              {
                title: "Container Cost Analysis",
                isMigrated: true,
                description: "Analysis of the spend on containerised workloads (ECS, EKS, ECR etc.)",
                iconName: "box",
                iconPack: "source-mid-render-light-icons",
                detailLevel: "HIGH",
                jobRoles: ["Engineering", "Operations", "FinOps"],
              },
              {
                title: "Cost by Resource Tags",
                iconName: "price-tag",
                iconPack: "source-mid-render-light-icons",
                detailLevel: "HIGH",
                jobRoles: ["Engineering", "Operations"],
                description:
                  "The report displays resource level costs by the resource Tag configured in your Master Account.",
                isMigrated: true,
                target:
                  "https://lookerstudio.google.com/embed/reporting/fa865367-b6f0-48b2-8009-d8064ed075c4/page/p_rokf0o2lwc",
                styles: {
                  minHeight: "310vh",
                },
              },
              {
                title: "Executive Dashboard",
                description: "High level overview of change in cost as well as analysis of Compute & Storage costs.",
                detailLevel: "LOW",
                jobRoles: ["Leadership", "FinOps"],
                isMigrated: true,
                iconName: "business",
                iconPack: "source-mid-render-light-icons",
                target:
                  "https://lookerstudio.google.com/embed/reporting/3a4dc621-5ba3-4cb9-a67e-ba75feeba30e/page/PmitC",
                styles: {
                  minHeight: "250vh",
                },
              },
              /* {
                title: "Cost per Product",
                description: "Usage cost sliced by AWS service (e.g. Lambda, S3...)",
                isMigrated: true,
                iconName: "settings",
                iconPack: "source-mid-render-light-icons",
                target:
                  "https://lookerstudio.google.com/embed/reporting/fa865367-b6f0-48b2-8009-d8064ed075c4/page/p_w2zh7v0lwc",
                styles: {
                  minHeight: "350vh",
                },
              }, */
              {
                title: "Cost Governance",
                isMigrated: true,
                description: "Aggregated cost optimisation dashboard",
                detailLevel: "LOW",
                isHidden: currentTenant === "trial",
                jobRoles: ["Operations", "Engineering", "FinOps"],
                iconName: "community",
                iconPack: "source-mid-render-light-icons",
                target:
                  "https://lookerstudio.google.com/embed/reporting/c919b1ad-d0c2-42e3-92dd-58803dfaba69/page/p_g9e396qj9c",
                styles: {
                  minHeight: "240vh",
                },
              },
              {
                title: "Cost per Master Account (Breakdown)",
                isMigrated: true,
                detailLevel: "LOW",
                jobRoles: ["Leadership", "Finance"],
                iconName: "savings",
                iconPack: "source-mid-render-light-icons",
                description: "Cost per AWS Master Account presented in a tabular format",
                target:
                  "https://lookerstudio.google.com/embed/reporting/fa865367-b6f0-48b2-8009-d8064ed075c4/page/p_97gzd8zlwc",
                styles: {
                  minHeight: "350vh",
                },
              },
              {
                title: "Forecasts",
                description: "See the predicted future costs of your accounts",
                detailLevel: "LOW",
                jobRoles: ["Leadership", "Finance", "FinOps"],
                iconName: "chart-line",
                iconPack: "source-mid-render-light-icons",
                isMigrated: true,
                target:
                  "https://lookerstudio.google.com/embed/reporting/fa865367-b6f0-48b2-8009-d8064ed075c4/page/p_adrtmf8l6c",
                styles: {
                  minHeight: "320vh",
                },
              },
              {
                title: "AWS Cost Anomaly Detection",
                description: "See the anomalies in your AWS accounts daily spending",
                detailLevel: "LOW",
                jobRoles: ["Leadership", "Finance", "FinOps"],
                isMigrated: true,
                isDisabled: !this.featureToggles.isFeatureEnabled(userInfo, "anomaly-detection"),
                iconName: "anomaly_detection",
                iconPack: "custom-icons",
              },
              {
                title: "Cloud Connect",
                isMigrated: true,
                iconName: "cloud-upload",
                detailLevel: "HIGH",
                jobRoles: ["Engineering", "Operations", "FinOps"],
                iconPack: "source-mid-render-light-icons",
                stage: currentTenant === "vodafone" ? "NEW" : undefined,
                description: "",
                target: "",
                styles: {
                  minHeight: "300vh",
                },
                isHidden: currentTenant !== "vodafone",
              },
              {
                title: "AWS Spend vs Commitment Dashboard",
                isMigrated: true,
                iconName: "bill",
                iconPack: "source-mid-render-light-icons",
                detailLevel: "LOW",
                jobRoles: ["Leadership", "Finance"],
                isHidden: !this.featureToggles.isFeatureEnabled(userInfo, "quarterly-billing-report"),
                description:
                  "In AWS billing happens at the master billing account level. Use this report to see the cost per master billing account for Quarterly Billing Report.",
                stage: currentTenant === "vodafone" ? "NEW" : undefined,
                styles: {
                  minHeight: "300vh",
                },
              },
            ],
          },
          {
            title: "Budgets",
            isDisabled: currentTenant !== "vodafone",
            isHidden: currentTenant !== "vodafone",
            pages: [
              {
                description:
                  "This service is being decommissioned. Please refer to the new service, Budgets & Alerts, for future use",
                isMigrated: true,
                iconName: "briefcase",
                detailLevel: "LOW",
                jobRoles: ["Leadership", "Engineering", "Operations", "FinOps"],
                iconPack: "source-mid-render-light-icons",
                title: "Budgets Overview",
                target:
                  "https://lookerstudio.google.com/embed/reporting/f4434adb-885a-4e31-8c1b-5ad0c5a56ded/page/p_f2ur51rlwc",
                styles: {
                  minHeight: "310vh",
                },
              },
              {
                isMigrated: true,
                isDisabled: true,
                title: "Budgets Variance",
                detailLevel: "LOW",
                jobRoles: ["Leadership", "Engineering", "Operations", "FinOps"],
                iconName: "make-payment",
                description:
                  "This service is being decommissioned. Please refer to the new service, Budgets & Alerts, for future use",
                iconPack: "source-mid-render-light-icons",
                target:
                  "https://lookerstudio.google.com/embed/reporting/f4434adb-885a-4e31-8c1b-5ad0c5a56ded/page/p_rb20unslwc",
                styles: {
                  minHeight: "350vh",
                },
              },
            ],
          },
          {
            title: "Asset Inventory",
            description:
              "Aggregated (multi-account and multi org.) asset inventories for various services & resource types. Use it to find resources and for security & compliance (CSB).",
            pages: [
              {
                title: "Inventory",
                iconName: "book",
                iconPack: "source-mid-render-light-icons",
                description:
                  "Aggregated (multi account, multi org.) view of all AWS assets. Search, filter, find, export..",
                isMigrated: true,
                detailLevel: "LOW",
                jobRoles: ["Engineering", "Operations", "Security", "FinOps"],
                target:
                  "https://lookerstudio.google.com/embed/reporting/1891a43e-6029-4f20-9336-464627d2a67c/page/p_i4lvk6qc2c",
                styles: {
                  minHeight: "450vh",
                },
              },
              {
                title: "EC2 Instances",
                iconName: "e-sim",
                iconPack: "source-mid-render-light-icons",
                detailLevel: "HIGH",
                jobRoles: ["Engineering", "Operations", "Security"],
                description:
                  "Aggregated (multi account, multi org.) view of all EC2 instances. Search, filter, find, export..",
                isMigrated: true,
                target:
                  "https://lookerstudio.google.com/embed/reporting/5ad2cf38-3700-4749-b188-f713b23fa203/page/p_ub44yu2ipc",
                styles: {
                  minHeight: "350vh",
                },
              },
              // {
              //   title: "EC2 Instances (Account)",
              //   iconName: "users",
              //   iconPack: "source-mid-render-light-icons",
              //   target:
              //     "https://lookerstudio.google.com/embed/reporting/5ad2cf38-3700-4749-b188-f713b23fa203/page/p_hxilvxjfpc",
              //   styles: {
              //     minHeight: "280vh",
              //   },
              //   isMigrated: true,
              //   isHidden: currentTenant !== "vodafone",
              // },
              // {
              //   title: "EC2 Instances (Images)",
              //   iconName: "dashboard",
              //   iconPack: "source-mid-render-light-icons",
              //   target:
              //     "https://lookerstudio.google.com/embed/reporting/5ad2cf38-3700-4749-b188-f713b23fa203/page/p_9ocghgogpc",
              //   styles: {
              //     minHeight: "280vh",
              //   },
              //   isMigrated: true,
              //   isHidden: currentTenant !== "vodafone",
              // },
              // {
              //   title: "EC2 Instances (Monitoring)",
              //   target:
              //     "https://lookerstudio.google.com/embed/reporting/5ad2cf38-3700-4749-b188-f713b23fa203/page/p_d22avbn6oc",
              //   styles: {
              //     minHeight: "310vh",
              //   },
              //   isMigrated: true,
              //   iconName: "security-camera",
              //   iconPack: "source-mid-render-light-icons",
              //   isHidden: currentTenant !== "vodafone",
              // },
              // {
              //   title: "EC2 Instances (State)",
              //   iconName: "e-sim",
              //   iconPack: "source-mid-render-light-icons",
              //   target:
              //     "https://lookerstudio.google.com/embed/reporting/5ad2cf38-3700-4749-b188-f713b23fa203/page/p_xmlahyogpc",
              //   styles: {
              //     minHeight: "310vh",
              //   },
              //   isMigrated: true,
              //   isHidden: currentTenant !== "vodafone",
              // },
              // {
              //   title: "EC2 Instances (Zone)",
              //   target:
              //     "https://lookerstudio.google.com/embed/reporting/5ad2cf38-3700-4749-b188-f713b23fa203/page/p_z8hze6m6oc",
              //   styles: {
              //     minHeight: "300vh",
              //   },
              //   iconName: "location",
              //   iconPack: "source-mid-render-light-icons",
              //   isMigrated: true,
              //   isHidden: currentTenant !== "vodafone",
              // },
              {
                title: "Terminated EC2 Instances",
                isMigrated: true,
                detailLevel: "HIGH",
                jobRoles: ["Engineering", "Operations", "Security"],
                iconName: "cancel",
                isHidden: currentTenant !== "vodafone",
                iconPack: "source-mid-render-light-icons",
                description:
                  "Aggregated (multi account, multi org.) view of all terminated EC2 instances. Search, filter, find, export..",
                target:
                  "https://lookerstudio.google.com/embed/reporting/fa865367-b6f0-48b2-8009-d8064ed075c4/page/p_69b0zl2ppc",
                styles: {
                  minHeight: "330vh",
                },
              },
              {
                title: "RDS Databases",
                iconName: "sme",
                detailLevel: "LOW",
                jobRoles: ["Engineering", "Operations", "Security", "Leadership"],
                iconPack: "source-mid-render-light-icons",
                description:
                  "Aggregated (multi account, multi org.) view of all RDS databases (MySQL, Postgres etc.). Search, filter, find, export..",
                isMigrated: true,
                target:
                  "https://lookerstudio.google.com/embed/reporting/fa865367-b6f0-48b2-8009-d8064ed075c4/page/p_qls2pd305c",
                styles: {
                  minHeight: "350vh",
                },
              },
              {
                title: "S3 Storage Buckets",
                iconName: "iot",
                iconPack: "source-mid-render-light-icons",
                detailLevel: "LOW",
                jobRoles: ["Engineering", "Operations", "Security"],
                isMigrated: true,
                description:
                  "Aggregated (multi account, multi org.) view of all S3 buckets. Search, filter, find, export..",
                isHidden: currentTenant === "trial",
                target:
                  "https://lookerstudio.google.com/embed/reporting/fa865367-b6f0-48b2-8009-d8064ed075c4/page/p_kgi7zhyc2c",
                styles: {
                  minHeight: "350vh",
                },
              },
            ],
          },
          {
            title: "Cloud Recommendations",
            description:
              "Reports that focus on cost optimisation and recommendations such as changing instance type, removing unused resources, purchasing a commitment to reduce cost etc.",
            pages: [
              {
                isMigrated: true,
                description: "See the detailed cost of compute services: EC2, Lambda & ECS.",
                title: "Cost by On-Demand Compute",
                jobRoles: ["Engineering", "Operations", "FinOps", "Security"],
                detailLevel: "HIGH",
                iconName: "e-sim",
                iconPack: "source-mid-render-light-icons",
                target:
                  "https://lookerstudio.google.com/embed/reporting/fa865367-b6f0-48b2-8009-d8064ed075c4/page/p_urtjlpnv0c",
                styles: {
                  minHeight: "310vh",
                },
              },
              {
                title: "EC2 Instance Rightsizing",
                isMigrated: true,
                description: "Analyse which instances are overprovisioned and can be resized to save money.",
                iconName: "control-panel",
                detailLevel: "HIGH",
                jobRoles: ["Engineering", "Operations", "FinOps"],
                iconPack: "source-mid-render-light-icons",
                target:
                  "https://lookerstudio.google.com/embed/reporting/fa865367-b6f0-48b2-8009-d8064ed075c4/page/p_x59xal4b6c",
                styles: {
                  minHeight: "400vh",
                },
              },
              {
                title: "Savings Plans",
                isMigrated: true,
                iconName: "celebrate",
                iconPack: "source-mid-render-light-icons",
                detailLevel: "HIGH",
                jobRoles: ["Engineering", "Operations", "FinOps"],
                description:
                  "Savings plans are commitments on the usage of compute (EC2, ECS, Lambda) resources. Click to see recommendations.",
                target:
                  "https://lookerstudio.google.com/embed/reporting/fa865367-b6f0-48b2-8009-d8064ed075c4/page/p_6b99r13b6c",
                styles: {
                  minHeight: "380vh",
                },
              },
              {
                title: "Underutilized EBS Volumes",
                description: "Analyse which storage disks can be removed and/or are overprovisioned.",
                iconName: "prepare",
                detailLevel: "HIGH",
                jobRoles: ["Engineering", "Operations", "FinOps"],
                iconPack: "source-mid-render-light-icons",
                target:
                  "https://lookerstudio.google.com/embed/reporting/fa865367-b6f0-48b2-8009-d8064ed075c4/page/p_4vedbb550c",
                styles: {
                  minHeight: "350vh",
                },
                isMigrated: true,
                isHidden: currentTenant === "trial",
              },
              {
                isMigrated: true,
                title: "Unassociated IP Addresses",
                iconName: "gravity",
                detailLevel: "HIGH",
                jobRoles: ["Engineering", "Operations", "FinOps"],
                description: "Analyse which IP addresses can be removed to save money.",
                iconPack: "source-mid-render-light-icons",
                target:
                  "https://lookerstudio.google.com/embed/reporting/fa865367-b6f0-48b2-8009-d8064ed075c4/page/p_ni83ha550c",
                styles: {
                  minHeight: "350vh",
                },
              },
              {
                title: "Reserved Instances Recommendations",
                isMigrated: true,
                iconName: "deals",
                detailLevel: "HIGH",
                jobRoles: ["Engineering", "Operations", "FinOps"],
                iconPack: "source-mid-render-light-icons",
                description:
                  "Reserved instances are commitments on the usage of specific EC2 instance types. A newer, more flexible version of such commitments are 'Savings Plans'. Click to see RI recommendations.",
                target:
                  "https://lookerstudio.google.com/embed/reporting/fa865367-b6f0-48b2-8009-d8064ed075c4/page/p_w4jm4bv7uc",
                styles: {
                  minHeight: "350vh",
                },
              },
              {
                title: "EKS Recommendations",
                isMigrated: true,
                iconName: "diagnostics",
                iconPack: "source-mid-render-light-icons",
                detailLevel: "HIGH",
                jobRoles: ["Engineering", "Operations", "FinOps"],
                // isHidden: currentTenant !== "vodafone",
                isHidden: true,
                description: "See recommendations related to compute resources uses by your EKS clusters.",
                target:
                  "https://lookerstudio.google.com/embed/reporting/fa865367-b6f0-48b2-8009-d8064ed075c4/page/p_n2mb0scr8c",
                styles: {
                  minHeight: "250vh",
                },
              },
              {
                title: "Cost Savings by PCS Scheduler",
                isMigrated: true,
                iconName: "clock",
                iconPack: "source-mid-render-light-icons",
                detailLevel: "HIGH",
                jobRoles: ["Engineering", "Operations", "FinOps"],
                description:
                  "This report shows you the cost reduction achieved by the PCS scheduler. To opt in click on Services > AWS > Scheduler.",
                target:
                  "https://lookerstudio.google.com/embed/reporting/fa865367-b6f0-48b2-8009-d8064ed075c4/page/p_hoqvdq7b6c",
                styles: {
                  minHeight: "300vh",
                },
                isHidden: currentTenant !== "vodafone",
              },
              {
                title: "Underutilized Endpoints",
                isMigrated: true,
                iconName: "international",
                detailLevel: "HIGH",
                jobRoles: ["Engineering", "Operations", "FinOps"],
                iconPack: "source-mid-render-light-icons",
                stage: currentTenant === "vodafone" ? "NEW" : undefined,
                description: "",
                target: "",
                styles: {
                  minHeight: "300vh",
                },
                isHidden: currentTenant !== "vodafone",
              },
              {
                isMigrated: true,
                description:
                  "Dashboard with Extended Support Charge Recommendations AWS projects, and support charge recommendations.",
                title: "Extended Support Charge Recommendations ",
                jobRoles: ["Engineering", "Operations", "FinOps", "Leadership"],
                detailLevel: "LOW",
                iconName: "dashboard",
                iconPack: "source-mid-render-light-icons",
                url: "/vcloudsmart/v2/aws/cloud-recommendations",
                styles: {
                  minHeight: "400vh",
                },
              },
            ],
          },
          //           {
          //             title: "Other",
          //             isHidden: currentTenant !== "vodafone",
          //             pages: [
          //               // {
          //               //   title: "Image Report I",
          //               //   isMigrated: false,
          //               //   iconName: "dashboard",
          //               //   iconPack: "source-mid-render-light-icons",
          //               //   isHidden: currentTenant !== "vodafone",
          //               //   target:
          //               //     "https://lookerstudio.google.com/embed/reporting/1e9383fe-a1e9-468b-a9fc-5e0cb2081c1e/page/p_dainx9zdtc",
          //               //   styles: {
          //               //     minHeight: "370vh",
          //               //   },
          //               // },
          //               {
          //                 title: "Image Analysis",
          //                 isMigrated: false,
          //                 iconName: "dashboard",
          //                 iconPack: "source-mid-render-light-icons",
          //                 isHidden: currentTenant !== "vodafone",
          //                 target:
          //                   "https://lookerstudio.google.com/embed/reporting/1e9383fe-a1e9-468b-a9fc-5e0cb2081c1e/page/p_esiip40dtc",
          //                 styles: {
          //                   minHeight: "350vh",
          //                 },
          //               },
          //             ],
          //           },
        ],
      },
      {
        id: "gcp",
        title: "Google Cloud Platform",
        isDisabled: !["vodafone", "vodacom_za"].includes(currentTenant),
        icon: "/assets/icons-home/gcp.svg",
        termsAndDefinitions: gcpDefinitions,
        sections: [
          {
            title: "Cost & Billing",
            description: "Reports related to cost & billing of resources, services, accounts etc. over time.",
            pages: [
              {
                title: "Overview",
                isMigrated: true,
                iconName: "calendar",
                iconPack: "source-mid-render-light-icons",
                detailLevel: "LOW",
                jobRoles: ["Finance", "Engineering", "Leadership", "FinOps"],
                description: "Summary of costs over time, top cost contributors & budgets.",
                target:
                  "https://lookerstudio.google.com/embed/reporting/c716631c-4608-4193-a53d-59f9410932ad/page/p_wq2fy4g1oc",
                styles: {
                  minHeight: "400vh",
                },
              },
              {
                isMigrated: true,
                iconName: "pie-chart",
                description: "INTERNAL and EXTERNAL charge analysis",
                iconPack: "source-mid-render-light-icons",
                title: "Charge Analysis (SAMS)",
                detailLevel: "HIGH",
                jobRoles: ["Finance", "Leadership"],
                target:
                  "https://lookerstudio.google.com/embed/reporting/c716631c-4608-4193-a53d-59f9410932ad/page/p_kpf8q93ooc",
                styles: {
                  minHeight: "350vh",
                },
                isHidden: currentTenant !== "vodafone",
              },
              {
                title: "Cost Analysis",
                description: "Analysis of cost by various attributes such as: Service, Project, Programme etc.",
                iconName: "chart-bar",
                iconPack: "source-mid-render-light-icons",
                isMigrated: true,
                detailLevel: "HIGH",
                jobRoles: ["Leadership", "Engineering", "Finance", "FinOps"],
                target:
                  "https://lookerstudio.google.com/embed/reporting/c716631c-4608-4193-a53d-59f9410932ad/page/p_4dbftpvv3c",
                styles: {
                  minHeight: "350vh",
                },
              },
              {
                title: "Executive Dashboard",
                isMigrated: true,
                description: "High level overview of change in cost as well as analysis of Compute & Storage costs.",
                iconName: "business",
                detailLevel: "LOW",
                jobRoles: ["Leadership", "FinOps"],
                iconPack: "source-mid-render-light-icons",
                target:
                  "https://lookerstudio.google.com/embed/reporting/8a6a9a5b-b94a-478d-9037-e645152d8109/page/p_7bi7wyitxc",
                styles: {
                  minHeight: "200vh",
                },
                isHidden: currentTenant !== "vodafone",
              },
              {
                title: "Cost by Label",
                isMigrated: true,
                iconName: "price-tag",
                detailLevel: "HIGH",
                jobRoles: ["Engineering", "Operations"],
                iconPack: "source-mid-render-light-icons",
                description:
                  "The report displays resource level costs by the resource label configured on the resource.",
                target:
                  "https://lookerstudio.google.com/embed/reporting/d973f81c-3708-4eff-82d5-9d1a2a380012/page/p_stgm8n6q5c",
                styles: {
                  minHeight: "250vh",
                },
                isHidden: currentTenant !== "vodafone",
              },
              {
                title: "Cost by Label V2",
                isMigrated: true,
                iconName: "price-tag",
                detailLevel: "HIGH",
                jobRoles: ["Engineering", "Operations"],
                iconPack: "source-mid-render-light-icons",
                description:
                  "Enhanced expandable table view grouped by label key, label value, project ID, and service.",
                target:
                  "https://lookerstudio.google.com/embed/reporting/d973f81c-3708-4eff-82d5-9d1a2a380012/page/p_stgm8n6q5c",
                styles: {
                  minHeight: "250vh",
                },
                isHidden: currentTenant !== "vodafone",
              },
              {
                title: "Other Org and Support Charges",
                isMigrated: true,
                iconName: "community",
                detailLevel: "LOW",
                jobRoles: ["Leadership", "FinOps", "Finance"],
                description: "Analysis of cost by other organisation and support charges",
                iconPack: "source-mid-render-light-icons",
                isHidden: currentTenant !== "vodafone",
                target:
                  "https://lookerstudio.google.com/embed/reporting/c716631c-4608-4193-a53d-59f9410932ad/page/p_i9ialmg7tc",
                styles: {
                  minHeight: "300vh",
                },
              },
              {
                title: "Cost by Billing Account",
                isMigrated: true,
                iconName: "bill",
                iconPack: "source-mid-render-light-icons",
                detailLevel: "LOW",
                jobRoles: ["Leadership", "Finance"],
                description:
                  "In GCP billing happens at the billing account level. Each billing account is associated to multiple GCP projects. Use this report to see the split of cost by billing account.",
                target:
                  "https://lookerstudio.google.com/embed/reporting/c716631c-4608-4193-a53d-59f9410932ad/page/p_zy4q4fybuc",
                styles: {
                  minHeight: "300vh",
                },
              },
              // {
              //   title: "Cost Breakdown",
              //   iconName: "savings",
              //   iconPack: "source-mid-render-light-icons",
              //   detailLevel: "LOW",
              //   jobRoles: ["Leadership", "Finance"],
              //   isMigrated: true,
              //   description: "Split of cost into different categories (Actual Cost, Credits, etc.)",
              //   target:
              //     "https://lookerstudio.google.com/embed/reporting/c716631c-4608-4193-a53d-59f9410932ad/page/ekPWC",
              //   styles: {
              //     minHeight: "180vh",
              //   },
              // },
              {
                title: "Cost Governance",
                isMigrated: true,
                description: "Aggregated cost optimisation dashboard",
                isHidden: ["trial", "vodacom_za"].includes(currentTenant),
                iconName: "community",
                jobRoles: ["Engineering", "Operations", "FinOps"],
                detailLevel: "LOW",
                iconPack: "source-mid-render-light-icons",
                target:
                  "https://lookerstudio.google.com/embed/reporting/c919b1ad-d0c2-42e3-92dd-58803dfaba69/page/p_g9e396qj9c",
                styles: {
                  minHeight: "240vh",
                },
              },
              {
                title: "Cost by Compute Instance Runtime",
                description: "Analyse the cost consumption of your Compute Instance virtual machines",
                iconName: "e-sim",
                iconPack: "source-mid-render-light-icons",
                jobRoles: ["Engineering", "Operations", "FinOps"],
                detailLevel: "HIGH",
                isMigrated: true,
                target:
                  "https://lookerstudio.google.com/embed/reporting/c716631c-4608-4193-a53d-59f9410932ad/page/p_92raj9302c",
                styles: {
                  minHeight: "220vh",
                },
                isHidden: currentTenant !== "vodafone",
              },
              {
                title: "Forecasts",
                description: "See the predicted future costs of your projects",
                iconName: "chart-line",
                iconPack: "source-mid-render-light-icons",
                detailLevel: "LOW",
                jobRoles: ["Leadership", "Finance", "FinOps"],
                isMigrated: true,
                styles: {
                  minHeight: "320vh",
                },
              },
              {
                title: "BigQuery Cost Analysis",
                isMigrated: true,
                iconName: "sme",
                detailLevel: "HIGH",
                jobRoles: ["Engineering", "Operations"],
                iconPack: "source-mid-render-light-icons",
                description:
                  "Analyse the cost of your BigQuery queries (cost per query, duration of queries, trend over time...)",
                isHidden: currentTenant !== "vodafone",
                target:
                  "https://lookerstudio.google.com/embed/reporting/c716631c-4608-4193-a53d-59f9410932ad/page/IdqeB",
                styles: {
                  minHeight: "240vh",
                },
              },
              {
                title: "GCP Cost Anomaly Detection",
                description: "See the anomalies in your GCP projects daily spending",
                detailLevel: "LOW",
                jobRoles: ["Leadership", "Finance", "FinOps"],
                isMigrated: true,
                isDisabled: !this.featureToggles.isFeatureEnabled(userInfo, "anomaly-detection"),
                stage: currentTenant === "vodafone" ? "NEW" : undefined,
                iconName: "anomaly_detection",
                iconPack: "custom-icons",
              },
              {
                title: "GCP Spend vs Commitment Dashboard",
                isMigrated: true,
                iconName: "bill",
                iconPack: "source-mid-render-light-icons",
                detailLevel: "LOW",
                jobRoles: ["Leadership", "Finance"],
                isHidden: !this.featureToggles.isFeatureEnabled(userInfo, "quarterly-billing-report"),
                description:
                  "In GCP billing happens at the billing account level. Use this report to see the cost per GCP billing account for Quarterly Billing Report.",
                stage: currentTenant === "vodafone" ? "NEW" : undefined,
                styles: {
                  minHeight: "300vh",
                },
              },
              {
                title: "Aggregated Discount Analysis by SKU Group",
                isMigrated: true,
                iconName: "calendar",
                iconPack: "source-mid-render-light-icons",
                detailLevel: "LOW",
                jobRoles: ["Leadership", "Finance", "FinOps"],
                description: "Aggregated discounts by SKU group.",
                stage: currentTenant === "vodafone" ? "NEW" : undefined,
                isDisabled: !this.featureToggles.isFeatureEnabled(userInfo, "gcp-monthly-aggregate"),
              },
              {
                title: "SKUs List Price Monitoring",
                isMigrated: true,
                iconName: "price-promise",
                iconPack: "source-mid-render-light-icons",
                detailLevel: "LOW",
                jobRoles: [],
                isHidden: currentTenant !== "vodafone",
                description: "",
                stage: currentTenant === "vodafone" ? "NEW" : undefined,
                styles: {
                  minHeight: "300vh",
                },
              },
            ],
          },
          {
            title: "Cloud Recommendations",
            description:
              "Reports that focus on cost optimisation and recommendations such as changing instance type, removing unused resources, purchasing a commitment to reduce cost etc.",
            isHidden: currentTenant !== "vodafone",
            pages: [
              {
                title: "Overview",
                isMigrated: true,
                jobRoles: ["Engineering", "Operations", "FinOps", "Security"],
                detailLevel: "LOW",
                iconName: "idea",
                iconPack: "source-mid-render-light-icons",
                isHidden: currentTenant !== "vodafone",
                target:
                  "https://lookerstudio.google.com/embed/reporting/c716631c-4608-4193-a53d-59f9410932ad/page/p_22ciwdqxgd",
                styles: {
                  minHeight: "450vh",
                },
              },
              {
                title: "Cost",
                isMigrated: true,
                jobRoles: ["Engineering", "FinOps"],
                detailLevel: "LOW",
                iconName: "savings",
                iconPack: "source-mid-render-light-icons",
                target:
                  "https://lookerstudio.google.com/embed/reporting/c716631c-4608-4193-a53d-59f9410932ad/page/p_22ciwdqxgd",
                styles: {
                  minHeight: "400vh",
                },
              },
              {
                title: "Security",
                isMigrated: true,
                iconName: "padlock-close",
                jobRoles: ["Engineering", "Operations", "Security"],
                detailLevel: "LOW",
                iconPack: "source-mid-render-light-icons",
                target:
                  "https://lookerstudio.google.com/embed/reporting/c716631c-4608-4193-a53d-59f9410932ad/page/p_22ciwdqxgd",
                styles: {
                  minHeight: "380vh",
                },
              },
              {
                title: "Performance",
                isMigrated: true,
                iconName: "control-panel",
                jobRoles: ["Engineering", "Operations", "FinOps"],
                detailLevel: "LOW",
                iconPack: "source-mid-render-light-icons",
                target:
                  "https://lookerstudio.google.com/embed/reporting/c716631c-4608-4193-a53d-59f9410932ad/page/p_22ciwdqxgd",
                styles: {
                  minHeight: "380vh",
                },
              },
              {
                title: "Reliability",
                isMigrated: true,
                isHidden: currentTenant !== "vodafone",
                iconName: "sme",
                jobRoles: ["Engineering", "Operations"],
                detailLevel: "LOW",
                iconPack: "source-mid-render-light-icons",
                target:
                  "https://lookerstudio.google.com/embed/reporting/c716631c-4608-4193-a53d-59f9410932ad/page/p_22ciwdqxgd",
                styles: {
                  minHeight: "380vh",
                },
              },
              {
                title: "Cost Savings by PCS Scheduler",
                isMigrated: true,
                iconName: "clock",
                iconPack: "source-mid-render-light-icons",
                detailLevel: "HIGH",
                jobRoles: ["Engineering", "Operations", "FinOps"],
                description:
                  "This report shows you the cost reduction achieved by the PCS scheduler. To opt in click on Services > GCP > Scheduler.",
                target: "",
                styles: {
                  minHeight: "300vh",
                },
                isHidden: currentTenant !== "vodafone",
              },
              {
                isMigrated: true,
                description:
                  "Dashboard with Extended Support Charge Recommendations GCP projects, and support charge recommendations.",
                title: "Extended Support Charge Recommendations",
                jobRoles: ["Engineering", "Operations", "FinOps", "Leadership"],
                detailLevel: "LOW",
                iconName: "dashboard",
                iconPack: "source-mid-render-light-icons",
                url: "/vcloudsmart/v2/gcp/cloud-recommendations",
                styles: {
                  minHeight: "400vh",
                },
              },
            ],
          },
          {
            title: "Budgets",
            isDisabled: currentTenant !== "vodafone",
            isHidden: currentTenant !== "vodafone",
            pages: [
              {
                title: "Budgets Overview",
                description:
                  "This service is being decommissioned. Please refer to the new service, Budgets & Alerts, for future use",
                isMigrated: true,
                detailLevel: "LOW",
                jobRoles: ["Leadership", "Engineering", "Operations", "FinOps"],
                iconName: "briefcase",
                iconPack: "source-mid-render-light-icons",
                isHidden: currentTenant !== "vodafone",
                target:
                  "https://lookerstudio.google.com/embed/reporting/c7b1ec35-88d7-4b79-9349-92d20f5f94cf/page/h9meB",
                styles: {
                  minHeight: "280vh",
                },
              },
              {
                title: "Budgets Variance",
                isDisabled: true,
                detailLevel: "LOW",
                jobRoles: ["Leadership", "Engineering", "Operations", "FinOps"],
                isMigrated: true,
                iconName: "make-payment",
                description:
                  "This service is being decommissioned. Please refer to the new service, Budgets & Alerts, for future use",
                iconPack: "source-mid-render-light-icons",
                isHidden: currentTenant !== "vodafone",
                target:
                  "https://lookerstudio.google.com/embed/reporting/c7b1ec35-88d7-4b79-9349-92d20f5f94cf/page/iyEmB",
                styles: {
                  minHeight: "310vh",
                },
              },
            ],
          },
          {
            title: "Asset Inventory",
            isHidden: currentTenant !== "vodafone",
            description:
              "Aggregated (multi-account and multi org.) asset inventories for various services & resource types. Use it to find resources and for security & compliance (CSB).",
            pages: [
              {
                title: "Overview",
                isMigrated: true,
                iconName: "book",
                iconPack: "source-mid-render-light-icons",
                description: "Aggregated (multi project) view of all GCP assets. Search, filter, find, export..",
                detailLevel: "LOW",
                jobRoles: ["Engineering", "Operations", "Security", "FinOps"],
                isHidden: currentTenant !== "vodafone",
                target:
                  "https://lookerstudio.google.com/embed/reporting/1057ab0a-ec5f-4274-be98-21b1a0037748/page/oypnC",
                styles: {
                  minHeight: "280vh",
                },
              },
              {
                title: "Compute Instances",
                isMigrated: true,
                description: "Aggregated (multi project) view of all Compute Instances. Search, filter, find, export..",
                iconName: "e-sim",
                detailLevel: "HIGH",
                jobRoles: ["Engineering", "Operations", "Security"],
                iconPack: "source-mid-render-light-icons",
                isHidden: currentTenant !== "vodafone",
                target:
                  "https://lookerstudio.google.com/embed/reporting/c716631c-4608-4193-a53d-59f9410932ad/page/p_8fhe3c105c",
                styles: {
                  minHeight: "250vh",
                },
              },
              {
                title: "Sustainability Report",
                isMigrated: true,
                detailLevel: "HIGH",
                jobRoles: ["Engineering", "Operations", "Sustainability", "FinOps", "Security"],
                iconName: "iot",
                iconPack: "source-mid-render-light-icons",
                isHidden: currentTenant !== "vodafone",
                description:
                  "Azure sustainability metrics and emissions tracking. Analyze resource efficiency and carbon footprint reduction opportunities.",
                styles: {
                  minHeight: "450vh",
                },
              },
              //               {
              //                 title: "Projects",
              //                 isMigrated: false,
              //                 iconName: "calendar-month",
              //                 iconPack: "source-mid-render-light-icons",
              //                 isHidden: currentTenant !== "vodafone",
              //                 target:
              //                   "https://lookerstudio.google.com/embed/reporting/1057ab0a-ec5f-4274-be98-21b1a0037748/page/p_kn23i659sc",
              //                 styles: {
              //                   minHeight: "250vh",
              //                 },
              //               },
              //               {
              //                 title: "Location",
              //                 isMigrated: false,
              //                 iconName: "location",
              //                 iconPack: "source-mid-render-light-icons",
              //                 isHidden: currentTenant !== "vodafone",
              //                 target:
              //                   "https://lookerstudio.google.com/embed/reporting/1057ab0a-ec5f-4274-be98-21b1a0037748/page/p_9l8p7z5l5c",
              //                 styles: {
              //                   minHeight: "180vh",
              //                 },
              //               },
            ],
          },

          // {
          //   title: "SAMS (VAMS)",
          //   isHidden: currentTenant !== "vodafone",
          //   description: "Financial reports",
          //   pages: [
          //     // {
          //     //   isMigrated: true,
          //     //   iconName: "pie-chart",
          //     //   description: "INTERNAL and EXTERNAL charge analysis",
          //     //   iconPack: "source-mid-render-light-icons",
          //     //   title: "SAMS Report",
          //     //   target:
          //     //     "https://lookerstudio.google.com/embed/reporting/c716631c-4608-4193-a53d-59f9410932ad/page/p_kpf8q93ooc",
          //     //   styles: {
          //     //     minHeight: "350vh",
          //     //   },
          //     // },
          //     {
          //       title: "Other Org and Support Charges",
          //       isMigrated: true,
          //       detailLevel: "HIGH",
          //       jobRoles: ["Finance", "Leadership", "FinOps"],
          //       iconName: "community",
          //       description: "Analysis of cost by other organisation and support charges",
          //       iconPack: "source-mid-render-light-icons",
          //       isHidden: currentTenant !== "vodafone",
          //       target:
          //         "https://lookerstudio.google.com/embed/reporting/c716631c-4608-4193-a53d-59f9410932ad/page/p_i9ialmg7tc",
          //       styles: {
          //         minHeight: "300vh",
          //       },
          //     },
          //     {
          //       title: "Billing Account",
          //       isMigrated: true,
          //       iconName: "bill",
          //       detailLevel: "HIGH",
          //       jobRoles: ["Finance", "Leadership", "FinOps"],
          //       iconPack: "source-mid-render-light-icons",
          //       description:
          //         "In GCP billing happens at the billing account level. Each billing account is associated to multiple GCP projects. Use this report to see the split of cost by billing account.",
          //       target:
          //         "https://lookerstudio.google.com/embed/reporting/c716631c-4608-4193-a53d-59f9410932ad/page/p_zy4q4fybuc",
          //       styles: {
          //         minHeight: "300vh",
          //       },
          //     },
          //   ],
          // },
          {
            title: "DataHub Cost Analysis",
            isHidden: currentTenant !== "vodafone",
            pages: [
              {
                title: "Overview",
                iconName: "public-sector",
                iconPack: "source-mid-render-light-icons",
                detailLevel: "HIGH",
                jobRoles: ["Leadership", "FinOps", "Engineering"],
                isMigrated: false,
                isHidden: currentTenant !== "vodafone",
                target:
                  "https://lookerstudio.google.com/embed/reporting/84f33885-2e6f-45be-9dbc-81ecbb587676/page/p_1zjx6w7ayc",
                styles: {
                  minHeight: "350vh",
                },
              },
              {
                title: "Actual Cost Distribution",
                isMigrated: false,
                iconName: "chart-line",
                iconPack: "source-mid-render-light-icons",
                detailLevel: "LOW",
                jobRoles: ["FinOps", "Engineering"],
                isHidden: currentTenant !== "vodafone",
                target:
                  "https://lookerstudio.google.com/embed/reporting/84f33885-2e6f-45be-9dbc-81ecbb587676/page/lJ3kB",
                styles: {
                  minHeight: "350vh",
                },
              },
              {
                title: "Storage Analysis",
                isMigrated: false,
                iconName: "sme",
                iconPack: "source-mid-render-light-icons",
                detailLevel: "HIGH",
                jobRoles: ["FinOps", "Engineering"],
                isHidden: currentTenant !== "vodafone",
                target:
                  "https://lookerstudio.google.com/embed/reporting/84f33885-2e6f-45be-9dbc-81ecbb587676/page/RuLiB",
                styles: {
                  minHeight: "230vh",
                },
              },
            ],
          },
          //           {
          //             title: "Other",
          //             isHidden: currentTenant !== "vodafone",
          //             pages: [
          //               // {
          //               //   title: "Image Report I",
          //               //   isMigrated: false,
          //               //   isHidden: currentTenant !== "vodafone",
          //               //   iconName: "dashboard",
          //               //   iconPack: "source-mid-render-light-icons",
          //               //   target:
          //               //     "https://lookerstudio.google.com/embed/reporting/1e9383fe-a1e9-468b-a9fc-5e0cb2081c1e/page/xU9MC",
          //               //   styles: {
          //               //     minHeight: "300vh",
          //               //   },
          //               // },
          //               {
          //                 title: "Image Analysis",
          //                 isMigrated: false,
          //                 isHidden: currentTenant !== "vodafone",
          //                 iconName: "dashboard",
          //                 iconPack: "source-mid-render-light-icons",
          //                 target:
          //                   "https://lookerstudio.google.com/embed/reporting/1e9383fe-a1e9-468b-a9fc-5e0cb2081c1e/page/xsbOC",
          //                 styles: {
          //                   minHeight: "350vh",
          //                 },
          //               },
          //             ],
          //           },
        ],
      },
      {
        id: "azure",
        title: "Microsoft Azure",
        icon: "/assets/icons-home/azure.svg",
        isDisabled: !["vodafone" /* , "vodacom_za" */].includes(currentTenant),
        termsAndDefinitions: azureDefinitions,
        sections: [
          {
            title: "Cost & Billing",
            description: "Reports related to cost & billing of resources, services, accounts etc. over time.",
            isHidden: currentTenant !== "vodafone",
            pages: [
              {
                title: "Overview",
                isMigrated: true,
                iconName: "calendar",
                iconPack: "source-mid-render-light-icons",
                isHidden: currentTenant !== "vodafone",
                detailLevel: "LOW",
                jobRoles: ["Finance", "Engineering", "Leadership", "FinOps"],
                target:
                  "https://lookerstudio.google.com/embed/reporting/e45719cf-1b4f-4d44-9614-8c6991276200/page/ZrmOC",
                styles: {
                  minHeight: "450vh",
                },
              },
              // {
              //   title: "Cost Per Subscription",
              //   isMigrated: true,
              //   iconName: "dashboard",
              //   iconPack: "source-mid-render-light-icons",
              //   detailLevel: "HIGH",
              //   jobRoles: ["Leadership", "Engineering", "Finance", "FinOps"],
              //   isHidden: currentTenant !== "vodafone",
              //   target:
              //     "https://lookerstudio.google.com/embed/reporting/e45719cf-1b4f-4d44-9614-8c6991276200/page/p_749taotiqc",
              //   styles: {
              //     minHeight: "350vh",
              //   },
              // },
              {
                title: "Cost Analysis",
                description: "Analysis of cost by various attributes such as: Service, Project, Programme etc.",
                isMigrated: true,
                detailLevel: "HIGH",
                jobRoles: ["Leadership", "Engineering", "Finance", "FinOps"],
                iconName: "chart-bar",
                iconPack: "source-mid-render-light-icons",
                isHidden: currentTenant !== "vodafone",
                target:
                  "https://lookerstudio.google.com/embed/reporting/e45719cf-1b4f-4d44-9614-8c6991276200/page/EUfOC",
                styles: {
                  minHeight: "380vh",
                },
              },
              {
                title: "Charge Analysis",
                description: "Analysis of cost by various attributes such as: Service, Project, Programme etc.",
                isMigrated: true,
                iconName: "chart-bar",
                detailLevel: "HIGH",
                jobRoles: ["Finance", "Leadership"],
                iconPack: "source-mid-render-light-icons",
                isHidden: currentTenant !== "vodafone",
                target:
                  "https://lookerstudio.google.com/embed/reporting/e45719cf-1b4f-4d44-9614-8c6991276200/page/EUfOC",
                styles: {
                  minHeight: "380vh",
                },
              },
              {
                title: "Executive Dashboard",
                description: "High level overview of change in cost as well as analysis of Compute & Storage costs.",
                isMigrated: true,
                iconName: "business",
                detailLevel: "LOW",
                jobRoles: ["Leadership", "FinOps"],
                iconPack: "source-mid-render-light-icons",
                styles: {
                  minHeight: "250vh",
                },
              },
              // {
              //   title: "Cost Breakdown",
              //   target:
              //     "https://lookerstudio.google.com/embed/reporting/e45719cf-1b4f-4d44-9614-8c6991276200/page/p_05yxqhjjqc",
              //   styles: {
              //     minHeight: "200vh",
              //   },
              //   isMigrated: false,
              //   iconName: "savings",
              //   detailLevel: "LOW",
              //   jobRoles: ["Leadership", "Finance"],
              //   iconPack: "source-mid-render-light-icons",
              //   isHidden: currentTenant !== "vodafone",
              // },
              {
                title: "Resource Cost by Custom Tag",
                iconName: "price-tag",
                iconPack: "source-mid-render-light-icons",
                detailLevel: "HIGH",
                jobRoles: ["Engineering", "Operations"],
                isMigrated: true,
                target:
                  "https://lookerstudio.google.com/embed/reporting/e45719cf-1b4f-4d44-9614-8c6991276200/page/p_u75ytojjqc",
                styles: {
                  minHeight: "280vh",
                },
              },
              {
                title: "Azure Cost Anomaly Detection",
                description: "See the anomalies in your Azure accounts daily spending",
                detailLevel: "LOW",
                jobRoles: ["Leadership", "Finance", "FinOps"],
                isMigrated: true,
                isDisabled: !this.featureToggles.isFeatureEnabled(userInfo, "anomaly-detection"),
                stage: currentTenant === "vodafone" ? "NEW" : undefined,
                iconName: "anomaly_detection",
                iconPack: "custom-icons",
              },
              {
                title: "Cloud Connect",
                isMigrated: true,
                iconName: "cloud-upload",
                detailLevel: "HIGH",
                jobRoles: ["Engineering", "Operations", "FinOps"],
                iconPack: "source-mid-render-light-icons",
                stage: currentTenant === "vodafone" ? "NEW" : undefined,
                description: "",
                target: "",
                styles: {
                  minHeight: "300vh",
                },
                isHidden: currentTenant !== "vodafone",
              },
              {
                title: "Forecasts",
                description: "See the predicted future costs of your projects",
                iconName: "chart-line",
                iconPack: "source-mid-render-light-icons",
                detailLevel: "LOW",
                jobRoles: ["Leadership", "Finance", "FinOps"],
                isMigrated: true,
                styles: {
                  minHeight: "320vh",
                },
              },
              {
                title: "Azure Spend vs Commitment Dashboard",
                isMigrated: true,
                iconName: "bill",
                iconPack: "source-mid-render-light-icons",
                detailLevel: "LOW",
                jobRoles: ["Leadership", "Finance"],
                isHidden: !this.featureToggles.isFeatureEnabled(userInfo, "quarterly-billing-report"),
                description:
                  "In Azure billing happens at the master billing account level. Use this report to see the cost per master billing account for Quarterly Billing Report.",
                stage: currentTenant === "vodafone" ? "NEW" : undefined,
                styles: {
                  minHeight: "300vh",
                },
              },
            ],
          },
          {
            title: "Asset Inventory",
            isHidden: currentTenant !== "vodafone",
            description:
              "Aggregated (multi-account and multi org.) asset inventories for various services & resource types. Use it to find resources and for security & compliance (CSB).",
            pages: [
              {
                title: "Inventory",
                isMigrated: true,
                detailLevel: "LOW",
                jobRoles: ["Engineering", "Operations", "Security", "FinOps"],
                iconName: "book",
                iconPack: "source-mid-render-light-icons",
                isHidden: currentTenant !== "vodafone",
                target:
                  "https://lookerstudio.google.com/embed/reporting/8c3c801a-8b9a-4bb6-8970-1dbd3ebe5265/page/p_qigl30bwpc",
                styles: {
                  minHeight: "450vh",
                },
              },
              {
                iconName: "loan",
                iconPack: "source-mid-render-light-icons",
                title: "Subscriptions",
                isMigrated: true,
                detailLevel: "LOW",
                jobRoles: ["Engineering", "Operations", "Security"],
                isHidden: currentTenant !== "vodafone",
                target:
                  "https://lookerstudio.google.com/embed/reporting/8c3c801a-8b9a-4bb6-8970-1dbd3ebe5265/page/p_7k70km1htc",
                styles: {
                  minHeight: "350vh",
                },
              },

              {
                title: "Sustainability Report",
                isMigrated: true,
                detailLevel: "HIGH",
                jobRoles: ["Engineering", "Operations", "Sustainability", "FinOps", "Security"],
                iconName: "iot",
                iconPack: "source-mid-render-light-icons",
                isHidden: currentTenant !== "vodafone",
                description:
                  "Azure sustainability metrics and emissions tracking. Analyze resource efficiency and carbon footprint reduction opportunities.",
                styles: {
                  minHeight: "450vh",
                },
              },
              {
                title: "Other",
                isMigrated: false,
                iconName: "placeholder",
                iconPack: "source-mid-render-light-icons",
                detailLevel: "HIGH",
                jobRoles: ["Engineering", "Operations", "Security"],
                // isHidden: currentTenant !== "vodafone",
                isHidden: true,
                target:
                  "https://lookerstudio.google.com/embed/reporting/8c3c801a-8b9a-4bb6-8970-1dbd3ebe5265/page/p_2ofqv1p1sc",
                styles: {
                  minHeight: "320vh",
                },
              },
            ],
          },
          {
            title: "SAMS (VAMS)",
            description: "Financial reports",
            isHidden: currentTenant !== "vodafone",
            pages: [
              {
                title: "SAMS Report",
                isMigrated: true,
                detailLevel: "HIGH",
                jobRoles: ["Finance", "Leadership"],
                iconName: "pie-chart",
                iconPack: "source-mid-render-light-icons",
                target:
                  "https://lookerstudio.google.com/embed/reporting/e45719cf-1b4f-4d44-9614-8c6991276200/page/p_4c5y5qgiqc",
                styles: {
                  minHeight: "450vh",
                },
              },
            ],
          },
          {
            title: "Cloud Recommendations",
            description:
              "Reports that focus on cost optimisation and recommendations such as changing instance type, removing unused resources, purchasing a commitment to reduce cost etc.",
            isHidden: currentTenant !== "vodafone",
            pages: [
              {
                title: "Overview",
                isMigrated: true,
                iconName: "idea",
                iconPack: "source-mid-render-light-icons",
                jobRoles: ["Engineering", "Operations", "FinOps", "Security"],
                detailLevel: "LOW",
                isHidden: currentTenant !== "vodafone",
                target:
                  "https://lookerstudio.google.com/embed/reporting/e45719cf-1b4f-4d44-9614-8c6991276200/page/p_3zfc3ujjqc",
                styles: {
                  minHeight: "330vh",
                },
              },
              {
                title: "Security",
                isHidden: currentTenant !== "vodafone",
                isMigrated: true,
                iconName: "padlock-close",
                jobRoles: ["Engineering", "Operations", "Security"],
                detailLevel: "LOW",
                iconPack: "source-mid-render-light-icons",
                target:
                  "https://lookerstudio.google.com/embed/reporting/e45719cf-1b4f-4d44-9614-8c6991276200/page/p_3zfc3ujjqc",
                styles: {
                  minHeight: "330vh",
                },
              },
              {
                title: "Operational Excellence",
                isMigrated: true,
                isHidden: currentTenant !== "vodafone",
                iconName: "padlock-close",
                jobRoles: ["Engineering", "Operations"],
                detailLevel: "LOW",
                iconPack: "source-mid-render-light-icons",
                target:
                  "https://lookerstudio.google.com/embed/reporting/e45719cf-1b4f-4d44-9614-8c6991276200/page/p_3zfc3ujjqc",
                styles: {
                  minHeight: "330vh",
                },
              },
              {
                title: "Performance",
                isMigrated: true,
                isHidden: currentTenant !== "vodafone",
                iconName: "control-panel",
                jobRoles: ["Engineering", "Operations", "FinOps"],
                detailLevel: "LOW",
                iconPack: "source-mid-render-light-icons",
                target:
                  "https://lookerstudio.google.com/embed/reporting/e45719cf-1b4f-4d44-9614-8c6991276200/page/p_3zfc3ujjqc",
                styles: {
                  minHeight: "330vh",
                },
              },
              {
                title: "High Availability",
                isMigrated: true,
                isHidden: currentTenant !== "vodafone",
                iconName: "sme",
                jobRoles: ["Engineering", "Operations"],
                detailLevel: "LOW",
                iconPack: "source-mid-render-light-icons",
                target:
                  "https://lookerstudio.google.com/embed/reporting/e45719cf-1b4f-4d44-9614-8c6991276200/page/p_3zfc3ujjqc",
                styles: {
                  minHeight: "330vh",
                },
              },
              {
                title: "Purchased Reservations",
                isMigrated: true,
                isHidden: currentTenant !== "vodafone",
                iconName: "deals",
                jobRoles: ["Engineering", "Operations", "FinOps"],
                detailLevel: "LOW",
                iconPack: "source-mid-render-light-icons",
                target:
                  "https://lookerstudio.google.com/embed/reporting/e45719cf-1b4f-4d44-9614-8c6991276200/page/p_coh5zo1eyc",
                styles: {
                  minHeight: "240vh",
                },
              },
               {
                title: "Cost Savings by PCS Scheduler",
                isMigrated: true,
                iconName: "clock",
                iconPack: "source-mid-render-light-icons",
                detailLevel: "HIGH",
                jobRoles: ["Engineering", "Operations", "FinOps"],
                description:
                  "This report shows you the cost reduction achieved by the PCS scheduler. To opt in click on Services > Azure > Scheduler.",
                target: "",
                styles: {
                  minHeight: "300vh",
                },
                isHidden: currentTenant !== "vodafone",
              },
            ],
          },
          {
            title: "Other",
            isHidden: currentTenant !== "vodafone",
            pages: [
              /* {
                title: "Commitment Analysis",
                iconName: "diagnostics",
                iconPack: "source-mid-render-light-icons",
                jobRoles: ["Engineering", "Operations", "FinOps"],
                detailLevel: "HIGH",
                isMigrated: false,
                isHidden: currentTenant !== "vodafone",
                target:
                  "https://lookerstudio.google.com/embed/reporting/e45719cf-1b4f-4d44-9614-8c6991276200/page/p_cue31sjjqc",
              }, */
              {
                title: "SKU Discount",
                iconName: "price-tag",
                iconPack: "source-mid-render-light-icons",
                isMigrated: true,
                jobRoles: ["Engineering", "Operations", "FinOps"],
                detailLevel: "HIGH",
                isHidden: currentTenant !== "vodafone",
                styles: {
                  minHeight: "450vh",
                },
                target:
                  "https://lookerstudio.google.com/embed/reporting/5bc3dd85-4d18-4c2f-9acd-291d97b92409/page/p_cue31sjjqc",
              },
            ],
          },
        ],
      },
      {
        id: "oci",
        title: "Oracle Cloud",
        icon: "/assets/icons-home/oracle.svg",
        isDisabled: !["vodafone" /* , "vodacom_za" */].includes(currentTenant),
        isHidden: false,
        termsAndDefinitions: ociDefinitions,
        sections: [
          {
            title: "Cost & Billing",
            description: "Reports related to cost & billing of resources, services, accounts etc. over time.",
            pages: [
              {
                title: "Overview",
                isMigrated: true,
                iconName: "calendar",
                detailLevel: "LOW",
                jobRoles: ["Finance", "Engineering", "Leadership", "FinOps"],
                iconPack: "source-mid-render-light-icons",
                target:
                  "https://lookerstudio.google.com/embed/reporting/5e4c46ce-8e9d-4d39-a837-2993ef4cbeed/page/p_ota1z242vc",
                styles: {
                  minHeight: "450vh",
                },
              },
              {
                title: "Cost Analysis",
                isMigrated: true,
                iconName: "chart-bar",
                iconPack: "source-mid-render-light-icons",
                detailLevel: "HIGH",
                jobRoles: ["Leadership", "Engineering", "Finance", "FinOps"],
                target:
                  "https://lookerstudio.google.com/embed/reporting/5e4c46ce-8e9d-4d39-a837-2993ef4cbeed/page/p_de6mkmf02c",
                styles: {
                  minHeight: "350vh",
                },
              },
            ],
          },
          {
            title: "Asset Inventory",
            description:
              "Aggregated (multi-account and multi org.) asset inventories for various services & resource types. Use it to find resources and for security & compliance (CSB).",
            pages: [
              {
                title: "Assets Overview",
                isMigrated: true,
                detailLevel: "LOW",
                jobRoles: ["Engineering", "Operations", "Security", "FinOps"],
                iconName: "book",
                iconPack: "source-mid-render-light-icons",
                target:
                  "https://lookerstudio.google.com/embed/reporting/5e4c46ce-8e9d-4d39-a837-2993ef4cbeed/page/p_yqsoa9sd8c",
                styles: {
                  minHeight: "450vh",
                },
              },
              {
                title: "File Systems",
                isMigrated: true,
                iconName: "document-zip",
                detailLevel: "LOW",
                jobRoles: ["Engineering", "Operations", "Security"],
                iconPack: "source-mid-render-light-icons",
                target:
                  "https://lookerstudio.google.com/embed/reporting/5e4c46ce-8e9d-4d39-a837-2993ef4cbeed/page/p_3yx6ahtd8c",
                styles: {
                  minHeight: "450vh",
                },
              },
              {
                title: "Virtual Networks",
                isMigrated: true,
                iconName: "network-tower",
                iconPack: "source-mid-render-light-icons",
                detailLevel: "HIGH",
                jobRoles: ["Engineering", "Operations", "Security"],
                target:
                  "https://lookerstudio.google.com/embed/reporting/5e4c46ce-8e9d-4d39-a837-2993ef4cbeed/page/p_wn6ubhtd8c",
                styles: {
                  minHeight: "450vh",
                },
              },
              {
                title: "Storage Volumes",
                isMigrated: true,
                iconName: "usb-modem",
                iconPack: "source-mid-render-light-icons",
                detailLevel: "LOW",
                jobRoles: ["Engineering", "Operations", "Security"],
                target:
                  "https://lookerstudio.google.com/embed/reporting/5e4c46ce-8e9d-4d39-a837-2993ef4cbeed/page/p_dz5l9gtd8c",
                styles: {
                  minHeight: "450vh",
                },
              },
              {
                title: "Compute Instances",
                isMigrated: true,
                iconName: "e-sim",
                detailLevel: "LOW",
                jobRoles: ["Engineering", "Operations", "Security", "Leadership"],
                iconPack: "source-mid-render-light-icons",
                target:
                  "https://lookerstudio.google.com/embed/reporting/5e4c46ce-8e9d-4d39-a837-2993ef4cbeed/page/p_z2o44gtd8c",
                styles: {
                  minHeight: "450vh",
                },
              },
              {
                title: "Database Systems",
                isMigrated: true,
                iconName: "sme",
                iconPack: "source-mid-render-light-icons",
                detailLevel: "LOW",
                jobRoles: ["Engineering", "Operations", "Security", "Leadership"],
                target:
                  "https://lookerstudio.google.com/embed/reporting/5e4c46ce-8e9d-4d39-a837-2993ef4cbeed/page/p_mhu57gtd8c",
                styles: {
                  minHeight: "450vh",
                },
              },
              {
                title: "Buckets",
                isMigrated: true,
                iconName: "iot",
                iconPack: "source-mid-render-light-icons",
                detailLevel: "LOW",
                jobRoles: ["Engineering", "Operations", "Security"],
                target:
                  "https://lookerstudio.google.com/embed/reporting/5e4c46ce-8e9d-4d39-a837-2993ef4cbeed/page/p_msibahtd8c",
                styles: {
                  minHeight: "450vh",
                },
              },
            ],
          },
          {
            title: "Recommendations",
            isHidden: currentTenant !== "vodafone",
            pages: [
              {
                title: "Overview",
                isMigrated: true,
                iconName: "idea",
                iconPack: "source-mid-render-light-icons",
                jobRoles: ["Engineering", "Operations", "FinOps", "Security"],
                detailLevel: "LOW",
                isHidden: currentTenant !== "vodafone",
                target:
                  "https://lookerstudio.google.com/embed/reporting/5e4c46ce-8e9d-4d39-a837-2993ef4cbeed/page/p_3q38mnwd8c",
                styles: {
                  minHeight: "450vh",
                },
              },
              {
                title: "Cost",
                isMigrated: true,
                jobRoles: ["Engineering", "FinOps"],
                detailLevel: "LOW",
                iconName: "savings",
                iconPack: "source-mid-render-light-icons",
                target:
                  "https://lookerstudio.google.com/embed/reporting/5e4c46ce-8e9d-4d39-a837-2993ef4cbeed/page/p_3q38mnwd8c",
                styles: {
                  minHeight: "400vh",
                },
              },
              {
                title: "Availability",
                isMigrated: true,
                iconName: "padlock-close",
                iconPack: "source-mid-render-light-icons",
                jobRoles: ["Engineering", "Operations"],
                detailLevel: "LOW",
                target:
                  "https://lookerstudio.google.com/embed/reporting/5e4c46ce-8e9d-4d39-a837-2993ef4cbeed/page/p_3q38mnwd8c",
                styles: {
                  minHeight: "380vh",
                },
              },
              {
                title: "Performance",
                isMigrated: true,
                iconName: "control-panel",
                iconPack: "source-mid-render-light-icons",
                jobRoles: ["Engineering", "Operations", "FinOps"],
                detailLevel: "LOW",
                target:
                  "https://lookerstudio.google.com/embed/reporting/5e4c46ce-8e9d-4d39-a837-2993ef4cbeed/page/p_3q38mnwd8c",
                styles: {
                  minHeight: "380vh",
                },
              },
            ],
          },
        ],
      },
      {
        id: "drcc",
        isHidden: currentTenant !== "vodafone",
        title: "Oracle Cloud DRCC",
        icon: "/assets/icons-home/DRCC.svg",
        termsAndDefinitions: drccDefinitions,
        sections: [
          {
            title: "Cost & Billing",
            description: "Reports related to cost & billing of resources, services, accounts etc. over time.",
            pages: [
              {
                title: "Overview",
                isMigrated: true,
                iconName: "calendar",
                iconPack: "source-mid-render-light-icons",
                detailLevel: "LOW",
                jobRoles: ["Finance", "Engineering", "Leadership", "FinOps"],
                target:
                  "https://lookerstudio.google.com/embed/reporting/21383948-d787-4bec-b8e7-944746ef76b2/page/p_h4jcbyikbd",
                styles: {
                  minHeight: "230vh",
                },
              },
              {
                title: "Cost Analysis",
                isMigrated: true,
                iconName: "search",
                iconPack: "source-mid-render-light-icons",
                detailLevel: "HIGH",
                jobRoles: ["Leadership", "Engineering", "Finance", "FinOps"],
                target:
                  "https://lookerstudio.google.com/embed/reporting/21383948-d787-4bec-b8e7-944746ef76b2/page/p_rd60g0ikbd",
                styles: {
                  minHeight: "300vh",
                },
              },
              {
                title: "Cost Breakdown",
                isMigrated: false,
                isHidden: true,
                iconName: "savings",
                iconPack: "source-mid-render-light-icons",
                detailLevel: "LOW",
                jobRoles: ["Leadership", "Finance"],
                target:
                  "https://lookerstudio.google.com/embed/reporting/21383948-d787-4bec-b8e7-944746ef76b2/page/p_u9t2s0ikbd",
                styles: {
                  minHeight: "270vh",
                },
              },
              {
                title: "DRCC Cost Anomaly Detection",
                description: "See the anomalies in your DRCC accounts daily spending",
                stage: currentTenant === "vodafone" ? "NEW" : undefined,
                isMigrated: true,
                isDisabled: !this.featureToggles.isFeatureEnabled(userInfo, "anomaly-detection"),
                iconName: "anomaly_detection",
                iconPack: "custom-icons",
                jobRoles: ["Finance", "Engineering", "Leadership", "FinOps"],
                detailLevel: "LOW",
                styles: {
                  minHeight: "300vh",
                },
              },
              {
                title: "Cost by Resource Tag",
                iconName: "price-tag",
                iconPack: "source-mid-render-light-icons" as IconPack,
                detailLevel: "LOW" as DetailLevel,
                jobRoles: ["Engineering", "Operations"] as JobRole[],
                isMigrated: true,
                target:
                  "https://lookerstudio.google.com/embed/reporting/21383948-d787-4bec-b8e7-944746ef76b2/page/p_vs1r90ikbd",
                styles: {
                  minHeight: "280vh",
                },
              },
              {
                title: "Forecast",
                iconName: "chart-line",
                iconPack: "source-mid-render-light-icons",
                isMigrated: true,
                detailLevel: "LOW",
                jobRoles: ["Leadership", "Finance", "FinOps"],
                target:
                  "https://lookerstudio.google.com/embed/reporting/21383948-d787-4bec-b8e7-944746ef76b2/page/p_e3u3sg6gbd",
                styles: {
                  minHeight: "320vh",
                },
              },
              {
                title: "Charge Analysis",
                iconName: "pie-chart",
                iconPack: "source-mid-render-light-icons",
                isDisabled: false,
                isMigrated: true,
                stage: currentTenant === "vodafone" ? "NEW" : undefined,
                description: "INTERNAL and EXTERNAL charge analysis",
                detailLevel: "HIGH",
                jobRoles: ["Finance", "Leadership"],
                target:
                  "https://lookerstudio.google.com/embed/reporting/c716631c-4608-4193-a53d-59f9410932ad/page/p_kpf8q93ooc",
                styles: {
                  minHeight: "350vh",
                },
              },
            ],
          },
          {
            title: "Asset Inventory",
            description:
              "Aggregated (multi-account and multi org.) asset inventories for various services & resource types. Use it to find resources and for security & compliance (CSB).",
            pages: [
              {
                title: "Overview",
                iconName: "book",
                iconPack: "source-mid-render-light-icons",
                detailLevel: "LOW",
                jobRoles: ["Engineering", "Operations", "Security", "FinOps"],
                isMigrated: true,
                target:
                  "https://lookerstudio.google.com/embed/reporting/21383948-d787-4bec-b8e7-944746ef76b2/page/p_4fcpmwxxbd",
                styles: {
                  minHeight: "270vh",
                },
              },
              {
                title: "Compute Instances",
                isMigrated: true,
                detailLevel: "LOW",
                jobRoles: ["Engineering", "Operations", "Security", "Leadership"],
                iconName: "e-sim",
                iconPack: "source-mid-render-light-icons",
                target:
                  "https://lookerstudio.google.com/embed/reporting/21383948-d787-4bec-b8e7-944746ef76b2/page/p_8lwqjftybd",
                styles: {
                  minHeight: "270vh",
                },
              },
              {
                title: "Database Systems",
                isMigrated: true,
                detailLevel: "LOW",
                jobRoles: ["Engineering", "Operations", "Security", "Leadership"],
                iconName: "sme",
                iconPack: "source-mid-render-light-icons",
                target:
                  "https://lookerstudio.google.com/embed/reporting/21383948-d787-4bec-b8e7-944746ef76b2/page/p_nh652ktybd",
                styles: {
                  minHeight: "270vh",
                },
              },
              {
                title: "Storage Volumes",
                isMigrated: true,
                iconName: "usb-modem",
                detailLevel: "LOW",
                jobRoles: ["Engineering", "Operations", "Security"],
                iconPack: "source-mid-render-light-icons",
                target:
                  "https://lookerstudio.google.com/embed/reporting/21383948-d787-4bec-b8e7-944746ef76b2/page/p_vowj9rwybd",
                styles: {
                  minHeight: "250vh",
                },
              },
              {
                title: "Object Storage Buckets",
                iconName: "iot",
                iconPack: "source-mid-render-light-icons",
                isMigrated: true,
                detailLevel: "LOW",
                jobRoles: ["Engineering", "Operations", "Security"],
                target:
                  "https://lookerstudio.google.com/embed/reporting/21383948-d787-4bec-b8e7-944746ef76b2/page/p_i28sbdyxbd",
                styles: {
                  minHeight: "250vh",
                },
              },
              {
                title: "File Systems",
                isMigrated: true,
                iconName: "document-zip",
                detailLevel: "LOW",
                jobRoles: ["Engineering", "Operations", "Security"],
                iconPack: "source-mid-render-light-icons",
                target:
                  "https://lookerstudio.google.com/embed/reporting/21383948-d787-4bec-b8e7-944746ef76b2/page/p_ots8aswybd",
                styles: {
                  minHeight: "250vh",
                },
              },
              {
                title: "Virtual Networks",
                iconName: "network-tower",
                iconPack: "source-mid-render-light-icons",
                detailLevel: "LOW",
                jobRoles: ["Engineering", "Operations", "Security"],
                isMigrated: true,
                target:
                  "https://lookerstudio.google.com/embed/reporting/21383948-d787-4bec-b8e7-944746ef76b2/page/p_psnhcswybd",
                styles: {
                  minHeight: "250vh",
                },
              },
            ],
          },
          {
            title: "Cloud Recommendations",
            description:
              "Reports that focus on cost optimisation and recommendations such as changing instance type, removing unused resources, purchasing a commitment to reduce cost etc.",
            pages: [
              {
                title: "Overview",
                isMigrated: true,
                iconName: "idea",
                jobRoles: ["Engineering", "Operations", "FinOps", "Security"],
                detailLevel: "LOW",
                iconPack: "source-mid-render-light-icons",
                isHidden: currentTenant !== "vodafone",
                target:
                  "https://lookerstudio.google.com/embed/reporting/5e4c46ce-8e9d-4d39-a837-2993ef4cbeed/page/p_3q38mnwd8c",
                styles: {
                  minHeight: "450vh",
                },
              },
              {
                title: "Cost Management",
                isMigrated: true,
                iconName: "savings",
                jobRoles: ["Engineering", "FinOps"],
                detailLevel: "LOW",
                iconPack: "source-mid-render-light-icons",
                target:
                  "https://lookerstudio.google.com/embed/reporting/5e4c46ce-8e9d-4d39-a837-2993ef4cbeed/page/p_3q38mnwd8c",
                styles: {
                  minHeight: "400vh",
                },
              },
              {
                title: "Performance",
                isMigrated: true,
                iconName: "control-panel",
                jobRoles: ["Engineering", "Operations", "FinOps"],
                detailLevel: "LOW",
                iconPack: "source-mid-render-light-icons",
                target:
                  "https://lookerstudio.google.com/embed/reporting/5e4c46ce-8e9d-4d39-a837-2993ef4cbeed/page/p_3q38mnwd8c",
                styles: {
                  minHeight: "380vh",
                },
              },
              {
                title: "High Availability",
                isMigrated: true,
                iconName: "padlock-close",
                iconPack: "source-mid-render-light-icons",
                jobRoles: ["Engineering", "Operations"],
                detailLevel: "LOW",
                target:
                  "https://lookerstudio.google.com/embed/reporting/5e4c46ce-8e9d-4d39-a837-2993ef4cbeed/page/p_3q38mnwd8c",
                styles: {
                  minHeight: "380vh",
                },
              },
              {
                title: "Cost Savings by PCS Scheduler",
                isMigrated: true,
                iconName: "clock",
                iconPack: "source-mid-render-light-icons",
                jobRoles: ["Engineering", "Operations", "FinOps"],
                detailLevel: "LOW",
                description:
                  "This report shows you the cost reduction achieved by the PCS scheduler. To opt in click on Services > DRCC > Scheduler.",
                target: "",
                styles: {
                  minHeight: "380vh",
                },
                isHidden: currentTenant !== "vodafone",
              },
            ],
          },
        ],
      },
      {
        id: "evo",
        title: "EVO Dashboard",
        icon: "/assets/icons-home/oracle.svg",
        isIconDisabled: true,
        isHidden: currentTenant !== "vodafone",
        isDisabled:
          currentTenant !== "vodafone" ||
          !(userInfo?.user?.tenancies ?? []).some((t) => t === "pcs" || t === "evo" || t === "CCE"),
        sections: [
          {
            title: "Cost & Billing",
            description: "Reports related to cost & billing of resources, services, accounts etc. over time.",
            pages: [
              {
                title: "Billing and Spend Management",
                isMigrated: true,
                iconName: "calculator",
                jobRoles: ["FinOps", "Leadership"],
                detailLevel: "HIGH",
                iconPack: "source-mid-render-light-icons",
                target:
                  "https://lookerstudio.google.com/embed/reporting/8631e8be-6752-4437-90a2-a9f6dc42e359/page/p_99a1pv3g4c",
                styles: {
                  minHeight: "350vh",
                },
              },
              {
                title: "Spend by Service view",
                isMigrated: true,
                iconName: "calculator",
                iconPack: "source-mid-render-light-icons",
                jobRoles: ["FinOps", "Leadership"],
                detailLevel: "LOW",
                target:
                  "https://lookerstudio.google.com/embed/reporting/8631e8be-6752-4437-90a2-a9f6dc42e359/page/p_99a1pv3g4c",
                styles: {
                  minHeight: "350vh",
                },
              },
              {
                title: "CUD View",
                isMigrated: true,
                iconPack: "source-mid-render-light-icons",
                jobRoles: ["FinOps"],
                detailLevel: "HIGH",
                iconName: "calculator",
                target:
                  "https://lookerstudio.google.com/embed/reporting/8631e8be-6752-4437-90a2-a9f6dc42e359/page/p_99a1pv3g4c",
                styles: {
                  minHeight: "350vh",
                },
              },
              {
                title: "Reservation By Project",
                isMigrated: true,
                iconName: "deals",
                jobRoles: ["FinOps"],
                detailLevel: "HIGH",
                iconPack: "source-mid-render-light-icons",
                target:
                  "https://lookerstudio.google.com/embed/reporting/8631e8be-6752-4437-90a2-a9f6dc42e359/page/p_99a1pv3g4c",
                styles: {
                  minHeight: "350vh",
                },
              },
              {
                title: "CUD Utilization",
                isMigrated: true,
                iconName: "deals",
                jobRoles: ["FinOps"],
                detailLevel: "HIGH",
                iconPack: "source-mid-render-light-icons",
                target:
                  "https://lookerstudio.google.com/embed/reporting/8631e8be-6752-4437-90a2-a9f6dc42e359/page/p_99a1pv3g4c",
                styles: {
                  minHeight: "350vh",
                },
              },
              {
                title: "Active and In-Active Machines",
                isMigrated: true,
                iconName: "deals",
                jobRoles: ["FinOps", "Engineering", "Operations"],
                detailLevel: "LOW",
                iconPack: "source-mid-render-light-icons",
                target:
                  "https://lookerstudio.google.com/embed/reporting/8631e8be-6752-4437-90a2-a9f6dc42e359/page/p_99a1pv3g4c",
                styles: {
                  minHeight: "350vh",
                },
              },
            ],
          },
        ],
      },
      {
        id: "multi",
        isHidden: currentTenant !== "vodafone",
        title: "Multi Cloud",
        isIconDisabled: true,
        sections: [
          {
            title: "Cost & Billing",
            description: "Reports related to cost & billing of resources, services, accounts etc. over time.",
            pages: [
              {
                title: "Cloud Spend Dashboard",
                isMigrated: true,
                iconName: "chart-bar",
                jobRoles: ["Leadership"],
                detailLevel: "LOW",
                iconPack: "source-mid-render-light-icons",
                styles: {
                  minHeight: "230vh",
                },
              },
            ],
          },
          {
            title: "Asset Inventory",
            description:
              "Aggregated (multi-account and multi org.) asset inventories for various services & resource types. Use it to find resources and for security & compliance (CSB).",
            pages: [
              {
                title: "Compute Dashboard",
                description: "",
                isMigrated: true,
                detailLevel: "LOW",
                jobRoles: ["Leadership", "Engineering", "Operations", "Security"],
                iconName: "dashboard",
                iconPack: "source-mid-render-light-icons",
                stage: currentTenant === "vodafone" ? "NEW" : undefined,
                target: "",
                styles: {
                  minHeight: "250vh",
                },
              },
            ],
          },
        ],
      },
      {
        id: "3rd party",
        isHidden: currentTenant !== "vodafone",
        title: "3rd Party",
        isIconDisabled: true,
        sections: [
          {
            title: "Mongo DB",
            description: "Reports related to Mongo DB",
            pages: [
              {
                title: "Cost Analysis",
                isMigrated: false,
                iconName: "chart-bar",
                jobRoles: ["FinOps", "Leadership"],
                detailLevel: "LOW",
                iconPack: "source-mid-render-light-icons",
                target:
                  "https://lookerstudio.google.com/embed/reporting/2a9109ac-00ad-46af-8639-4ddb73d670a4/page/p_aswrk8h0cd",
                styles: {
                  minHeight: "230vh",
                },
              },
            ],
          },
          {
            title: "Datadog",
            description: "Reports related to Datadog",
            pages: [
              {
                title: "Cost Analysis",
                isMigrated: false,
                jobRoles: ["FinOps", "Leadership"],
                detailLevel: "LOW",
                iconName: "chart-bar",
                iconPack: "source-mid-render-light-icons",
                target:
                  "https://lookerstudio.google.com/embed/reporting/2d244717-9e75-46b8-b289-2e5d2aecc77a/page/MdknD",
                styles: {
                  minHeight: "230vh",
                },
              },
            ],
          },
        ],
      },
    ];
  }

  private injectRecentReports(vcsConfig: Csp[]) {
    for (const csp of vcsConfig || []) {
      const previousReports: string = localStorage.getItem(`recentlyVisited/${csp.id}`);
      if (!previousReports) {
        continue;
      }
      const parsed: Array<{ title: string; section: string; csp: string }> = JSON.parse(previousReports) || [];
      if (!Array.isArray(parsed) || parsed.length === 0) {
        continue;
      }
      const newSection: Section = {
        title: "Recently Visited",
        isCollapsed: false,
        pages: [],
      };
      for (const section of csp?.sections || []) {
        if (section?.title === "Recently Visited" || section?.title === "Custom Reports") {
          continue;
        }
        for (const page of section?.pages || []) {
          if (
            !parsed.some((p) => {
              return (
                p.csp === csp?.id &&
                transformLegacySectionName(section?.title ?? "") === transformLegacySectionName(p.section) &&
                transformLegacyPageName(page?.title ?? "") === transformLegacyPageName(p.title)
              );
            })
          ) {
            continue;
          }
          newSection.pages.push({
            ...page,
            url: `/vcloudsmart${page.isMigrated ? "/v2" : ""}/${csp.id}/${transformLegacySectionName(
              section.title,
            )}/${transformLegacyPageName(page.title)}`,
          });
        }
      }
      if (newSection.pages.length > 0) {
        csp.sections = [newSection, ...(csp?.sections ?? [])];
      }
    }
  }

  private injectPrivateCustomReports(
    vcsConfig: Csp[],
    customReportsPrivateLite: ReportShare[],
    customReportsPrivateRegular: CustomVCSReport[],
    email: string,
  ) {
    for (const csp of vcsConfig || []) {
      const newSection: Section = {
        title: "Custom Reports",
        isCollapsed: false,
        description:
          "A custom report is created by sharing it. Shared reports can be named and retain their filters for easy access.",
        pages: [],
      };
      for (const section of csp?.sections || []) {
        if (section?.title === "Recently Visited" || section?.title === "Custom Reports") {
          continue;
        }
        for (const page of section?.pages || []) {
          for (const r of customReportsPrivateLite) {
            const [customReportCsp, customReportSection, customReportPage] = (r as ReportShare).Target.split("/")
              .filter(Boolean)
              .filter((s) => s !== "v2" && s !== "vcloudsmart");
            if (
              customReportCsp !== csp?.id ||
              customReportSection !== transformLegacySectionName(section?.title) ||
              customReportPage !== transformLegacyPageName(page?.title)
            ) {
              continue;
            }
            newSection.pages.push({
              ...page,
              title: r.DisplayName ?? `Custom ${page.title}`,
              customReportId: r.PK.replace("s#", ""),
              author: r.CreatedBy,
              queryParams: {
                shareId: r.PK.replace("s#", ""),
              },
              url: (r as ReportShare).Target,
            });
          }
        }
      }
      for (const r of customReportsPrivateRegular) {
        const customReportCsp: CloudProvider = r?.CloudProvider;
        if (customReportCsp !== csp?.id) {
          continue;
        }
        let reportTitle = r?.DisplayName ?? "Custom Report";
        try {
          reportTitle +=
            (r?.CreatedBy || "").toLowerCase() === email.toLowerCase()
              ? ""
              : ` (shared by ${this.formatting.prettifyAttribute((r?.CreatedBy || "").split("@")[0].split(".")[0])})`;
        } catch (e) {
          this.log.error(e);
        }
        const customReportId: string = r.PK.replace("s#", "");
        newSection.pages.push({
          ...this.getIconForReport(r),
          detailLevel: this.getCustomReportDetailLevel(r),
          isMigrated: true,
          jobRoles: this.getJobRolesForCustomReport(r),
          title: reportTitle,
          customReportId,
          url: `/vcloudsmart/v2/${customReportCsp}/custom-reports/${customReportId}`,
          author: r.CreatedBy,
          queryParams: {
            shareId: r.PK,
          },
        });
      }
      if (newSection.pages.length > 0) {
        csp.sections = [newSection, ...(csp?.sections ?? [])];
      }
    }
  }

  private initialiseUrls(vcsConfig: Csp[]) {
    for (const csp of vcsConfig || []) {
      for (const section of csp?.sections || []) {
        for (const page of section?.pages || []) {
          page.url = this.getReportLink(csp, section, page);
        }
      }
    }
  }

  private injectPublicCustomReports(vcsConfig: Csp[], customReportsPublic: CustomVCSReport[]) {
    for (const r of customReportsPublic) {
      const c: Csp = vcsConfig.find((csp) => csp.id === r.CloudProvider);
      if (!c) {
        continue;
      }
      let s: Section = (c?.sections ?? []).find((s) => s.title === r.Section);
      if (!s) {
        s = {
          title: r.Section,
          isCollapsed: false,
          description: `${this.formatting.prettifyAttribute(r.Section)} section`,
          pages: [],
        };
        c.sections.push(s);
      }
      if (s) {
        const reportTypePretty: string = this.formatting.prettifyAttribute(r.DataSourceType);
        const customReportId: string = r.PK.replace("s#", "");
        const p: Page = {
          ...this.getIconForReport(r),
          detailLevel: this.getCustomReportDetailLevel(r),
          isMigrated: true,
          jobRoles: this.getJobRolesForCustomReport(r),
          title: r.DisplayName ?? `Custom ${reportTypePretty} Report`,
          customReportId,
          url: `/vcloudsmart/v2/${r.CloudProvider}/custom-reports/${customReportId}`,
          description: `${reportTypePretty} report using ${r.DataSource} data`.trim(),
        };
        s.pages.push(p);
      }
    }
  }

  public getVCSConfigV3(
    currentTenant: string,
    addRecent: boolean = false,
    options: { userInfo?: UserPermissions; customReports?: CustomReport[] } = undefined,
  ): Observable<Array<Csp>> {
    return forkJoin({
      userInfo: options?.userInfo
        ? of(options?.userInfo)
        : this.authorisation.getUserPermissions({
            attributes: ["allowed_tenants", "current_tenant", "groups", "permissions", "tenancies"],
          }),
      customReportsAll: options?.customReports ? of(options?.customReports) : this.getCustomReports(),
    }).pipe(
      map(({ userInfo, customReportsAll }) => {
        const customReportsPrivate: CustomReport[] = (customReportsAll || []).filter((r: CustomReport) => {
          return (r as CustomVCSReport).Public !== "true";
        });
        const customReportsPublic: CustomVCSReport[] = (customReportsAll || []).filter((r: CustomReport) => {
          return (r as CustomVCSReport).Public === "true";
        }) as CustomVCSReport[];
        const customReportsPrivateLite: ReportShare[] = customReportsPrivate.filter((r) => {
          return !!(r as ReportShare).Target;
        }) as ReportShare[];
        const customReportsPrivateRegular: CustomVCSReport[] = customReportsPrivate.filter((r) => {
          return !(r as ReportShare).Target;
        }) as CustomVCSReport[];

        const vcsConfig: Csp[] = this.baseCsps(currentTenant, userInfo);

        this.injectPublicCustomReports(vcsConfig, customReportsPublic);

        if (customReportsPrivateLite.length + customReportsPrivateRegular.length > 0) {
          this.injectPrivateCustomReports(
            vcsConfig,
            customReportsPrivateLite,
            customReportsPrivateRegular,
            (userInfo?.user?.email || "").toLowerCase(),
          );
        }

        if (addRecent) {
          this.injectRecentReports(vcsConfig);
        }

        this.initialiseUrls(vcsConfig);

        return vcsConfig;
      }),
    );
  }

  private getVCSConfigV2Helper(
    currentTenant: string,
    userInfo: UserPermissions,
    addRecent: boolean = false,
    customReports: CustomReport[] = undefined,
  ): Array<Csp> {
    if (customReports === undefined) {
      customReports = [];
    }

    const vcsConfig: Csp[] = this.baseCsps(currentTenant, userInfo);

    this.initialiseUrls(vcsConfig);

    if (customReports.length > 0) {
      this.injectPublicCustomReports(
        vcsConfig,
        customReports.filter((r) => (r as CustomVCSReport).Public === "true") as CustomVCSReport[],
      );

      this.injectPrivateCustomReports(
        vcsConfig,
        customReports.filter((r) => {
          return (r as CustomVCSReport).Public !== "true" && !!(r as ReportShare).Target;
        }) as ReportShare[],
        customReports.filter((r) => {
          return (r as CustomVCSReport).Public !== "true" && !(r as ReportShare).Target;
        }) as CustomVCSReport[],
        (userInfo?.user?.email || "").toLowerCase(),
      );
    }

    if (addRecent) {
      this.injectRecentReports(vcsConfig);
    }

    return vcsConfig;
  }

  public getReportLink(csp: Csp, section: Section, page: Page): string {
    if (page.url) {
      return page.url;
    }
    const pageTitle: string = transformLegacyPageName(page?.title);
    const sectionTitle: string = transformLegacySectionName(section?.title);
    const suffix: string = `${csp?.id}/${sectionTitle}/${pageTitle}`;
    const preference: boolean = this.getUserSelectedPagesVersion()?.[suffix] ?? true;
    if (!page.target || (page?.isMigrated && preference)) {
      page.url = `/vcloudsmart/v2/${suffix}`;
    } else {
      page.url = `/vcloudsmart/${suffix}`;
    }
    return page.url;
  }

  public getPage(
    currentTenant: string | undefined,
    csp: string,
    section: string,
    page: string,
    user: UserPermissions,
    vcsConfig?: Array<Csp>,
  ): Page | undefined {
    return this.getParams(currentTenant, csp, section, page, user, vcsConfig).page;
  }

  public getParams(
    currentTenant: string | undefined,
    csp: string,
    section: string,
    page: string,
    user: UserPermissions,
    vcsConfig?: Array<Csp>,
  ): { csp: Csp; section: Section; page: Page } | undefined {
    for (const c of vcsConfig ?? this.getVCSConfigV2(currentTenant, user)) {
      if (c.id === csp) {
        for (const s of c.sections) {
          if (s.title === section) {
            for (const p of s.pages) {
              if (p.title === page) {
                return { page: p, section: s, csp: c };
              }
            }
          }
        }
      }
    }
  }

  public getUserSelectedPagesVersion(): { [key: string]: boolean } {
    try {
      const prefs = localStorage.getItem("vCloudSmartSelectedVersion");
      if (prefs) {
        return JSON.parse(prefs);
      }
    } catch (e) {
      this.log.error(e);
    }
    const newPrefs = {};
    localStorage.setItem("vCloudSmartSelectedVersion", JSON.stringify(newPrefs));
    return newPrefs;
  }

  public setUserSelectedPagesVersion(csp: string, section: string, page: string, selectMigrated: boolean) {
    let userSelectedVersion = {};
    const savedUserSelectedVersion = JSON.parse(localStorage.getItem("vCloudSmartSelectedVersion"));

    if (savedUserSelectedVersion) {
      userSelectedVersion = { ...savedUserSelectedVersion };
    }

    userSelectedVersion[`${csp}/${section}/${page}`] = selectMigrated;
    localStorage.setItem("vCloudSmartSelectedVersion", JSON.stringify(userSelectedVersion));
  }

  private getCustomReportDetailLevel(r: CustomVCSReport): DetailLevel {
    if (r?.DetailLevel) {
      return r.DetailLevel;
    }
    let sections: CustomSection[] = [];
    if (
      r?.Sections !== undefined &&
      // @ts-ignore
      r?.Sections !== "undefined"
    ) {
      try {
        const newSections: CustomSection[] = typeof r.Sections === "string" ? JSON.parse(r.Section) : r.Sections;
        if (Array.isArray(newSections)) {
          sections = newSections;
        }
      } catch (e) {
        this.log.debug(e);
      }
    }
    if (sections.length > 5) {
      return "HIGH";
    }
    return "LOW";
  }

  private getJobRolesForCustomReport(r: CustomVCSReport): JobRole[] {
    if (r?.JobRoles) {
      return r.JobRoles;
    } else if (r.DataSourceType === "billing") {
      return ["Leadership", "FinOps", "Finance"];
    } else if (r.DataSourceType === "assets") {
      return ["Operations", "Security"];
    } else if (r.DataSourceType === "recommendations") {
      return ["Operations", "Sustainability", "Procurement", "FinOps"];
    } else {
      return [];
    }
  }

  public getIconForReport(r: { IconName?: string; IconPack?: IconPack; DataSourceType?: QueryDomain }): {
    iconName: string;
    iconPack: IconPack;
  } {
    let defaultIconName = r?.IconName;
    if (!defaultIconName) {
      if (r.DataSourceType === "billing") {
        defaultIconName = "chart-bar";
      } else if (r.DataSourceType === "assets") {
        defaultIconName = "sme";
      } else if (r.DataSourceType === "recommendations") {
        defaultIconName = "idea";
      } else {
        defaultIconName = "pie-chart";
      }
    }
    return {
      iconName: defaultIconName || r.IconName,
      iconPack: r.IconPack || DEFAULT_ICON_PACK,
    };
  }
}
